// Hash-based router for Atlas.
//
// Why hash-based and not history-based:
//   • Babel-standalone, no build step, no server rewrites — we'd have to
//     teach Render's static site to serve index.html for every path, and
//     every path-style route would 404 on direct load otherwise.
//   • Hash routing works on plain static hosting with no config.
//   • Trade-off: URLs have a `#` in them. We're fine with that for a CRM.
//
// URL ↔ state mapping:
//   #/                       → {view: 'home'}
//   #/home                   → {view: 'home'}
//   #/deals                  → {view: 'deals'}
//   #/deals/<id>             → {view: 'deal',    dealId:    <id>}
//   #/companies              → {view: 'companies'}
//   #/companies/<id>         → {view: 'company', companyId: <id>}
//   #/people                 → {view: 'people'}
//   #/people/<id>            → {view: 'contact', contactId: <id>}
//   #/notes/<id>             → {view: 'notes',   noteId:    <id>}
//   #/cim/<id>               → {view: 'cim',     cimId:     <engagement id>}
//   #/ask/<id>               → {view: 'ask',     askId:     <shared Ask Atlas thread>}
//     The CIM Command Center for one seller deal. Its tab rides a query the
//     page writes itself (#/cim/<id>?tab=room) — see writeHash.
//   #/id/<number>            → {view: 'record',  recordNo:  <number>}
//     A record's ID (the number on its page; the same number DealCloud uses,
//     unique across companies, deals and contacts). app.jsx looks it up and
//     swaps in the canonical link. #/companies/<number> etc. work the same way.
//   #/notes, #/files, ...    → {view: <section>}
//
// Co-existence with the Supabase magic-link callback:
//   Login lands users on `…/#access_token=…&refresh_token=…`. login.jsx
//   strips that hash during AuthGate's useState initializer, before App
//   ever mounts — but we belt-and-brace by ignoring any hash that contains
//   `access_token=` in parseHash().

(function () {
  // Sections that map straight to a view name with no payload id.
  const SECTION_VIEWS = new Set([
    'home', 'deals', 'companies', 'people', 'notes', 'files', 'cleanup',
    'referrals', 'gpp', 'referral-partners', 'events', 'prospecting', 'inbox', 'settings', 'agents', 'lost-deals', 'integrations', 'signals', 'calling',
    'dashboard', 'buyers', 'activity', 'analytics', 'views', 'calendar',
    'team-pulse', 'daily-reports', 'marketing', 'blast', 'email-copilot', 'industries',
  ]);

  // Two-segment routes: /<head>/<id> → view + id field name
  const DETAIL_ROUTES = {
    deals:      { view: 'deal',      idField: 'dealId' },
    companies:  { view: 'company',   idField: 'companyId' },
    people:     { view: 'contact',   idField: 'contactId' },
    users:      { view: 'user',      idField: 'userId' },
    workspaces: { view: 'workspace', idField: 'workspaceId' },
    views:      { view: 'view',      idField: 'viewId' },
    // The buyer page (202609231400): #/buyers stays the roster, #/buyers/<id>
    // is one firm.
    buyers:     { view: 'buyer',     idField: 'buyerId' },
    // The CIM Command Center (202610131300): one seller deal's CIM build.
    cim:        { view: 'cim',       idField: 'cimId' },
    // Industry pages (202611021400): #/industries is the list,
    // #/industries/<slug> one industry.
    industries: { view: 'industry',  idField: 'industrySlug' },
    // An Ask Atlas thread its owner shared by link (202611031000) — read-only
    // for everyone else. There's no #/ask list: sharing is by link only.
    ask:        { view: 'ask',       idField: 'askId' },
    // A note has no page of its own — it opens as a modal over the Notes
    // list. So this route keeps the 'notes' section view (the sidebar stays
    // highlighted, back lands on the list) and carries the note to open.
    // This is the link the note modal's Share button hands out.
    notes:      { view: 'notes',     idField: 'noteId' },
    id:         { view: 'record',    idField: 'recordNo' },
  };

  // A record ID rather than an Atlas uuid — what people copy off a record page.
  const isRecordNumber = (v) => typeof v === 'string' && /^[0-9]{1,12}$/.test(v);

  function parseHash() {
    if (typeof window === 'undefined') return null;
    const raw = window.location.hash || '';
    if (!raw) return null;
    // Supabase magic-link callback — leave it alone.
    if (raw.includes('access_token=')) return null;
    // Only act on our own format (#/something). Anything else is "not ours".
    if (!raw.startsWith('#/')) return null;

    let path = raw.slice(2);                // strip "#/"
    if (!path) return { view: 'home' };

    // A view may refine its section route with a query the app-level state
    // doesn't model (e.g. #/marketing?channel=call). Route on the path alone;
    // the owning view reads window.location.hash itself for the refinement.
    const q = path.indexOf('?');
    if (q !== -1) path = path.slice(0, q);
    if (!path) return { view: 'home' };

    const parts = path.split('/').map(s => {
      try { return decodeURIComponent(s); } catch { return s; }
    });
    const [head, id] = parts;

    if (id && DETAIL_ROUTES[head]) {
      return { view: DETAIL_ROUTES[head].view, [DETAIL_ROUTES[head].idField]: id };
    }
    if (SECTION_VIEWS.has(head)) {
      return { view: head };
    }
    // Unknown route — let caller decide what to do (we just return null).
    return null;
  }

  // Section hashes whose sub-path is modelled by app state (see writeHash).
  const OWNED_SUBPATHS = new Set(['/notes']);

  function buildHash(state) {
    const { view, dealId, companyId, contactId, userId, workspaceId, viewId, noteId, buyerId, cimId, recordNo, industrySlug, askId } = state || {};

    if (view === 'deal'    && dealId)    return '/deals/'     + encodeURIComponent(dealId);
    if (view === 'company' && companyId) return '/companies/' + encodeURIComponent(companyId);
    if (view === 'contact' && contactId) return '/people/'    + encodeURIComponent(contactId);
    if (view === 'user'    && userId)    return '/users/'     + encodeURIComponent(userId);
    if (view === 'workspace' && workspaceId) return '/workspaces/' + encodeURIComponent(workspaceId);
    if (view === 'view'    && viewId)    return '/views/'     + encodeURIComponent(viewId);
    if (view === 'buyer'   && buyerId)   return '/buyers/'    + encodeURIComponent(buyerId);
    if (view === 'cim'     && cimId)     return '/cim/'       + encodeURIComponent(cimId);
    if (view === 'industry' && industrySlug) return '/industries/' + encodeURIComponent(industrySlug);
    if (view === 'ask'     && askId)     return '/ask/'       + encodeURIComponent(askId);
    // Checked before the SECTION_VIEWS fallback below, which would otherwise
    // flatten an open note back to a bare '/notes'.
    if (view === 'notes'   && noteId)    return '/notes/'     + encodeURIComponent(noteId);
    if (view === 'record'  && recordNo)  return '/id/'        + encodeURIComponent(recordNo);

    // Home stays hash-less — keeps the URL bar clean for the most common
    // landing state. Direct visits to `…/#/home` still parse and immediately
    // normalize to no-hash via the write effect in app.jsx.
    if (view === 'home') return '';

    if (view && SECTION_VIEWS.has(view)) return '/' + view;
    return '';
  }

  // Update the URL without firing hashchange recursively. Only writes if
  // the value actually differs — guards against pushState spam when several
  // setters fire in the same tick and the effect runs multiple times.
  function writeHash(state) {
    const next = buildHash(state);
    const current = (window.location.hash || '').slice(1);
    if (current === next) return false;
    // A view may refine its own section hash with a sub-path or query the
    // app-level state doesn't model (e.g. an open dashboard writes
    // #/analytics/dashboards/<id>?f=… — see DashboardCanvas). That still maps
    // to the same section here, so don't clobber the refinement.
    //
    // OWNED_SUBPATHS opts a section out: '/notes/<id>' IS app state (noteId),
    // so shrinking to '/notes' means the note was closed — a real navigation
    // to write, not a refinement to protect.
    //
    // The same goes for every DETAIL route: '/buyers/<id>' shrinking to
    // '/buyers' is the buyer page closing (buyerId is app state), so it is
    // written, not protected — otherwise the breadcrumb back to a list would
    // leave the record's URL in the bar. A detail page's own '?' refinement
    // (#/cim/<id>?tab=room) is still protected like any other.
    const ownsDetail = current[next.length] === '/' && DETAIL_ROUTES[next.slice(1)] != null;
    if (next && !OWNED_SUBPATHS.has(next) && !ownsDetail
        && current.startsWith(next) && (current[next.length] === '/' || current[next.length] === '?')) return false;
    // pushState gives the user a back-button entry per navigation. That
    // matches what people expect from "I clicked into a deal, hit back."
    try {
      if (next === '') {
        // Strip the hash entirely for home so the URL is just …/atlas.
        history.pushState(null, '', window.location.pathname + window.location.search);
      } else {
        history.pushState(null, '', '#' + next);
      }
    } catch (_) {
      // Some test environments / unusual contexts forbid pushState — fall
      // back to direct assignment, which still updates the hash.
      window.location.hash = next;
    }
    return true;
  }

  window.AtlasRouter = { parseHash, buildHash, writeHash, isRecordNumber, SECTION_VIEWS };
})();
